import { StandardEvents } from '@shopify/events';

// AllTerra Central — Product media overlay + no-media fallback (<at-product-badges>)
// 1) When the product has media, relocates the badge overlay (In stock / custom
//    badge) on top of Horizon's media column (.product-information__media).
// 2) When the product has NO media, Horizon drops the media column entirely and
//    centers the details. This injects a "No media" placeholder panel so the PDP
//    keeps its two-column layout. No theme files are edited.
// 3) Both badges are variant-level (stock comes from the selected variant, the
//    custom badge from custom.badge on the variant then the product), so the
//    overlay is rebuilt from the page Horizon fetches on `shopify:product:select`.
//    This block can't use <at-variant-reactive> like the other PDP blocks: by the
//    time the event fires the overlay no longer lives inside this element.
const NO_MEDIA_ICON = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <path d="M21 15l-4.5-4.5L6 21"/>
    <path d="M3 3l18 18"/>
  </svg>`;

class AtProductBadges extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || document;
    this.ensureMediaColumn();
    this.relocateBadges();

    this.onSelect = this.onSelect.bind(this);
    this.section.addEventListener(StandardEvents.productSelect, this.onSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }

  async onSelect(event) {
    const origin = event.target;
    if (origin instanceof Element && origin.closest('product-card')) return;

    let detail;
    try {
      ({ detail } = await event.promise);
    } catch {
      return; // Unavailable option combination — keep the current badges.
    }

    const html = detail?.html;
    if (!html) return;
    if (this.dataset.productId && detail.productId && detail.productId !== this.dataset.productId) return;

    // Drop the relocated overlay wherever it currently lives, then re-render this
    // element's contents from the freshly fetched page and relocate again.
    this.section.querySelectorAll('.product-information__media > .at-product-badges__overlay').forEach((el) => el.remove());

    const next = html.querySelector('at-product-badges');
    this.innerHTML = next ? next.innerHTML : '';
    this.relocateBadges();
  }

  // Build a placeholder media column when the product has no images, so the grid
  // stays two-column instead of centering the details.
  ensureMediaColumn() {
    const grid = this.section.querySelector('.product-information__grid');
    if (!grid || grid.querySelector('.product-information__media')) return;

    const placeholder = document.createElement('div');
    placeholder.className = 'product-information__media at-pdp-media--placeholder';
    placeholder.setAttribute('data-testid', 'product-information-media');
    placeholder.innerHTML =
      `<div class="at-pdp-media__empty">${NO_MEDIA_ICON}<span>No media</span></div>`;

    // Swap Horizon's "no media" (centered details) for its standard media-left
    // two-column layout, then insert the placeholder as the media column.
    grid.classList.remove('product-information--media-none');
    grid.classList.add('product-information--media-left');
    grid.prepend(placeholder);
  }

  relocateBadges() {
    const overlay = this.querySelector('.at-product-badges__overlay');
    if (!overlay) return;

    const media = this.section.querySelector('.product-information__media');
    if (!media) return;

    // Avoid duplicates if the details column re-renders (variant change).
    media.querySelectorAll(':scope > .at-product-badges__overlay').forEach((el) => el.remove());

    if (getComputedStyle(media).position === 'static') {
      media.style.position = 'relative';
    }
    media.appendChild(overlay);
  }
}

if (!customElements.get('at-product-badges')) {
  customElements.define('at-product-badges', AtProductBadges);
}
